import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Copy, ArrowLeft } from 'lucide-react';

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'object' && error !== null) {
    const err = error as { message?: unknown };
    if (typeof err.message === 'string' && err.message.length > 0) return err.message;
  }
  return fallback;
};

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  className?: string;
}

const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  showBackButton,
  onBack,
  className = '',
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`bg-app-surface-elevated border-border/30 text-text-primary max-w-md ${className}`}
      >
        <DialogHeader className="relative">
          {showBackButton && (
            <button
              data-test-id="modal-back-btn"
              onClick={onBack}
              className="absolute left-0 top-0 p-2 text-text-secondary hover:text-text-primary transition-smooth"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="ml-2 text-sm">Back</span>
            </button>
          )}

          {title && (
            <DialogTitle className="text-center text-text-primary font-bold text-xl">
              {title}
            </DialogTitle>
          )}
        </DialogHeader>

        <div className="mt-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
};

// League Created Modal
interface LeagueCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  pin: string;
  onViewLeague: () => void;
}

export const LeagueCreatedModal: React.FC<LeagueCreatedModalProps> = ({
  isOpen,
  onClose,
  pin,
  onViewLeague,
}) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(pin);
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="LEAGUE CREATED!">
      <div className="text-center space-y-6">
        <p className="text-text-secondary">
          Show this PIN to invite friends to your private league
        </p>

        {/* PIN Display */}
        <div className="flex justify-center gap-2">
          {pin.split('').map((char, index) => (
            <div
              key={index}
              className="w-12 h-12 bg-app-surface rounded-lg flex items-center justify-center text-text-primary font-bold text-xl"
            >
              {char}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div>
          <Button
            data-test-id="copy-pin-btn"
            variant="secondary"
            onClick={handleCopy}
            className="w-full gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy
          </Button>
        </div>

        <p className="text-text-muted text-sm">
          You can always copy your code later on the friends leaderboard page
        </p>

        <Button
          data-test-id="view-league-btn"
          variant="bet99"
          onClick={onViewLeague}
          className="w-full"
        >
          View League
        </Button>
      </div>
    </BaseModal>
  );
};

// Create Friends League Modal
interface CreateLeagueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (title: string) => void | Promise<void>;
  isSubmitting?: boolean;
}

export const CreateLeagueModal: React.FC<CreateLeagueModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const isSubmitDisabled = isSubmitting || localSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInFlightRef.current) return;

    const trimmed = title.trim();
    if (trimmed.length < 3 || trimmed.length > 100) {
      setError('League name must be between 3 and 100 characters');
      return;
    }
    setError(null);
    submitInFlightRef.current = true;
    setLocalSubmitting(true);
    try {
      await onSubmit(trimmed);
      setTitle('');
    } catch (submitError) {
      setError(extractErrorMessage(submitError, 'Failed to create league. Please try again.'));
    } finally {
      submitInFlightRef.current = false;
      setLocalSubmitting(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={() => {
        setTitle('');
        setError(null);
        onClose();
      }}
      title="CREATE A LEAGUE"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <p className="text-text-secondary text-center">
          Give your private league a name your friends will recognise.
        </p>
        <Input
          data-test-id="create-league-title-input"
          value={title}
          onChange={e => setTitle(e.target.value)}
          maxLength={100}
          placeholder="League name"
          autoFocus
          className="bg-app-surface border-border/30 text-text-primary"
        />
        {error && <p className="text-destructive text-sm text-center">{error}</p>}
        <Button
          data-test-id="create-league-submit-btn"
          type="submit"
          variant="bet99"
          disabled={isSubmitDisabled || title.trim().length === 0}
          className="w-full"
        >
          {isSubmitDisabled ? 'Creating...' : 'Create League'}
        </Button>
      </form>
    </BaseModal>
  );
};

// Join Friends League Modal
interface JoinLeagueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (code: string) => void | Promise<void>;
  isSubmitting?: boolean;
}

const JOIN_CODE_LENGTH = 6;

export const JoinLeagueModal: React.FC<JoinLeagueModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const isSubmitDisabled = isSubmitting || localSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInFlightRef.current) return;

    const normalised = code.trim().toUpperCase();
    if (normalised.length !== JOIN_CODE_LENGTH) {
      setError(`PIN must be ${JOIN_CODE_LENGTH} characters`);
      return;
    }
    setError(null);
    submitInFlightRef.current = true;
    setLocalSubmitting(true);
    try {
      await onSubmit(normalised);
      setCode('');
    } catch (submitError) {
      setError(extractErrorMessage(submitError, 'Unable to join league. Please try again.'));
    } finally {
      submitInFlightRef.current = false;
      setLocalSubmitting(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={() => {
        setCode('');
        setError(null);
        onClose();
      }}
      title="JOIN A LEAGUE"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <p className="text-text-secondary text-center">
          Enter the 6-character PIN a friend shared with you.
        </p>
        <Input
          data-test-id="join-league-code-input"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          maxLength={JOIN_CODE_LENGTH}
          placeholder="ABC123"
          autoFocus
          className="bg-app-surface border-border/30 text-text-primary text-center tracking-[0.4em] text-xl font-mono uppercase"
        />
        {error && <p className="text-destructive text-sm text-center">{error}</p>}
        <Button
          data-test-id="join-league-submit-btn"
          type="submit"
          variant="bet99"
          disabled={isSubmitDisabled || code.trim().length !== JOIN_CODE_LENGTH}
          className="w-full"
        >
          {isSubmitDisabled ? 'Joining...' : 'Join League'}
        </Button>
      </form>
    </BaseModal>
  );
};

// Picks Submitted Modal
interface PicksSubmittedModalProps {
  isOpen: boolean;
  onClose: () => void;
  potentialWinnings: string;
  onAddToBetSlip: () => void;
  onReturnToLobby: () => void;
}

export const PicksSubmittedModal: React.FC<PicksSubmittedModalProps> = ({
  isOpen,
  onClose,
  potentialWinnings,
  onAddToBetSlip,
  onReturnToLobby,
}) => {
  const { t } = useTranslation();

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={t('modal.picksSubmitted.title')}>
      <div className="text-center space-y-6">
        <p className="text-text-secondary">
          {t('modal.picksSubmitted.message')}
          <br />
          {t('modal.picksSubmitted.editMessage')}
        </p>

        <div className="bg-app-surface rounded-xl p-4">
          <p className="text-text-secondary text-sm mb-2">Like your picks?</p>
          <p className="text-text-secondary text-sm mb-4">
            Build a bet slip with the same selections.
          </p>

          <div className="space-y-2">
            <p className="text-text-primary font-bold text-sm">YOU COULD WIN</p>
            <p className="text-text-primary font-bold text-3xl">{potentialWinnings}</p>
            <p className="text-text-secondary text-sm">from a $10 bet</p>
          </div>
        </div>

        <div className="space-y-3">
          <Button variant="bet99" onClick={onAddToBetSlip} className="w-full">
            Add to Bet Slip
          </Button>
          <Button variant="secondary" onClick={onReturnToLobby} className="w-full">
            {t('modal.picksSubmitted.returnToLobby')}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

// Clear Selections Modal
interface ClearSelectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClearPicks: () => void;
}

export const ClearSelectionsModal: React.FC<ClearSelectionsModalProps> = ({
  isOpen,
  onClose,
  onClearPicks,
}) => {
  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="CLEAR SELECTIONS">
      <div className="text-center space-y-6">
        <p className="text-text-secondary">
          This action clears all current selections.
          <br />
          Are you sure you would like to clear all your picks?
        </p>

        <div className="space-y-3">
          <Button variant="bet99" onClick={onClearPicks} className="w-full">
            Clear Pick
          </Button>
          <Button variant="secondary" onClick={onClose} className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default BaseModal;
